// Anonymous read of a single shared patch for the `/s/<id>` route.
//
// RLS on `patches` allows anon SELECT of public/unlisted rows only, so
// a private or missing id resolves to null (maybeSingle returns no
// row) — the banner then shows a friendly "not found" state. The
// author handle + play count are embedded the same way the community
// listing joins them (worker/src/patches.ts:93-104).

import { getSupabase } from '../auth/client';

export type SharedPatch = {
  id: string;
  title: string;
  code: string;
  ownerId: string;
  authorHandle: string;
  playsCount: number;
};

export type SharedPatchRow = {
  id: string;
  title: string;
  code: string;
  owner_id: string;
  // to-one join → object (or null); non-inner so a missing profile
  // doesn't drop the patch.
  profiles?: { handle?: string | null; display_name?: string | null } | null;
  // embedded aggregate → single-element array.
  patch_plays?: Array<{ count: number }> | null;
};

export function mapSharedPatchRow(row: SharedPatchRow): SharedPatch {
  return {
    id: row.id,
    title: row.title,
    code: row.code,
    ownerId: row.owner_id,
    authorHandle:
      row.profiles?.handle ?? row.profiles?.display_name ?? 'anon',
    playsCount: row.patch_plays?.[0]?.count ?? 0,
  };
}

export async function getSharedPatch(id: string): Promise<SharedPatch | null> {
  const supabase = getSupabase();
  if (!supabase) return null;
  const { data, error } = await supabase
    .from('patches')
    .select(
      'id, title, code, owner_id, profiles(handle, display_name), patch_plays(count)',
    )
    .eq('id', id)
    .maybeSingle();
  if (error) throw new Error(`shared patch fetch failed: ${error.message}`);
  if (!data) return null;
  return mapSharedPatchRow(data as SharedPatchRow);
}
